import { ApiType, cmOptions, Parameter } from '@/pages/workgroup/tab/restPP/type.ts';
import ReactCodeMirror from '@uiw/react-codemirror';
import { StreamLanguage } from '@codemirror/language';
import { json } from '@codemirror/legacy-modes/mode/javascript';
import { Tab, Tabs } from 'baseui/tabs-motion';
import { FileTabsStyleObject, FileTabStyleObject } from '@/components/graphEditor/fileStore/styleObject.ts';
import { xcodeLight } from '@uiw/codemirror-theme-xcode';
import { Heading } from '@/pages/workgroup/tab/restPP/Util.tsx';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { forwardRef, useEffect, useState } from 'react';
import ContentCopy from '@/assets/content-copy.svg';
import useCopyClipboard from 'react-use-clipboard';
import showToast from '@/components/styledToasterContainer/showToast.tsx';
import { PLACEMENT, TRIGGER_TYPE } from 'baseui/popover';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { StatefulPopover } from '@tigergraph/app-ui-lib/popover';
import { generateJsonFromParams } from '@/utils/apiConnector';

export interface ApiSettingProps {
  apiType: ApiType;
  curlCommand?: string;
  javascriptCommand?: string;
  pythonCommand?: string;
  onQueryCodeChange?: (value: string) => void;
  apiInfo: { [key: string]: any };
  queryParams: Parameter[];
}

const InfoWithRef = forwardRef((props: any, ref) => {
  const { children, ...restProps } = props;
  return (
    <div ref={ref} {...restProps}>
      {children}
    </div>
  );
});

export function ApiSetting({
  apiType,
  apiInfo,
  curlCommand,
  pythonCommand,
  javascriptCommand,
  onQueryCodeChange,
  queryParams,
}: ApiSettingProps) {
  const [css, theme] = useStyletron();
  const [activeKey, setActiveKey] = useState('cURL');
  const [curlCopied, setCurlCopied] = useCopyClipboard(curlCommand || '', {
    successDuration: 1000,
  });
  const [javascriptCopied, setJavascriptCopied] = useCopyClipboard(javascriptCommand || '', {
    successDuration: 1000,
  });
  const [pythonCopied, setPythonCopied] = useCopyClipboard(pythonCommand || '', {
    successDuration: 1000,
  });
  const [queryCode, setQueryCode] = useState<string>('');
  const [hasQueryCodeChanged, setHasQueryCodeChanged] = useState<boolean>(false);

  useEffect(() => {
    if (curlCopied || javascriptCopied || pythonCopied) {
      showToast({
        kind: 'positive',
        message: 'Code snippet copied successfully.',
      });
    }
  }, [curlCopied, javascriptCopied, pythonCopied]);

  useEffect(() => {
    if (!hasQueryCodeChanged) {
      setQueryCode(generateJsonFromParams(queryParams));
      onQueryCodeChange && onQueryCodeChange(generateJsonFromParams(queryParams));
    }
  }, [hasQueryCodeChanged, onQueryCodeChange, queryParams]);

  const handleCopy = () => {
    switch (activeKey) {
      case 'cURL':
        setCurlCopied();
        break;
      case 'JavaScript':
        setJavascriptCopied();
        break;
      case 'Python':
        setPythonCopied();
        break;
    }
  };

  const handleQueryCodeChange = (value: string) => {
    setHasQueryCodeChanged(true);
    if (queryCode === value) {
      return;
    }
    setQueryCode(value);
    onQueryCodeChange && onQueryCodeChange(value);
  };

  return (
    <>
      <div>
        <div
          className={css({
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            marginBottom: '8px',
          })}
        >
          <Heading
            $style={{
              marginBottom: '0px',
              marginRight: '8px',
            }}
          >
            {apiType !== ApiType.GET ? 'JSON Payload' : 'Query String'}
          </Heading>
          {queryParams.length > 0 && (
            <StatefulPopover
              content={() => (
                <div>
                  <div>{'{'}</div>
                  {queryParams &&
                    queryParams.map((param) => (
                      <div
                        key={param.name}
                        className={css({
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '8px',
                        })}
                      >
                        <div>
                          &nbsp;&nbsp; &quot;{param.name}&quot;: {param.type}
                        </div>
                      </div>
                    ))}
                  <div>{'}'}</div>
                </div>
              )}
              triggerType={TRIGGER_TYPE.hover}
              placement={PLACEMENT.rightTop}
              ignoreBoundary
              showArrow={false}
              overrides={{
                Body: {
                  style: {
                    backgroundColor: theme.colors.white,
                    width: '200px',
                  },
                },
                Inner: {
                  style: {
                    backgroundColor: theme.colors.white,
                    color: theme.colors.black,
                  },
                },
              }}
            >
              <InfoWithRef>
                <FaRegQuestionCircle color={theme.colors.gray600} />
              </InfoWithRef>
            </StatefulPopover>
          )}
        </div>
        <ReactCodeMirror
          value={queryCode}
          onChange={(value) => handleQueryCodeChange(value)}
          basicSetup={cmOptions}
          width={'100%'}
          height={'120px'}
          extensions={[StreamLanguage.define(json)]}
        />
      </div>
      <div
        className={css({
          position: 'relative',
        })}
      >
        <img
          onClick={handleCopy}
          src={ContentCopy}
          className={css({
            cursor: 'pointer',
            position: 'absolute',
            right: '0',
            top: '50px',
          })}
        />
        <Heading>Code Snippet</Heading>
        <Tabs
          renderAll
          activeKey={activeKey}
          onChange={({ activeKey }) => {
            setActiveKey(activeKey as string);
          }}
          overrides={{
            ...FileTabsStyleObject(theme),
            TabList: {
              style: {
                height: '57px',
                paddingTop: '20px',
                paddingBottom: '0px',
                marginBottom: '0px',
                width: 'calc(100% - 180px)',
                overflowX: 'scroll',
              },
            },
          }}
        >
          <Tab key={'cURL'} overrides={FileTabStyleObject(theme)} title={'cURL'}>
            <div
              className={css({
                width: '100%',
                height: '100%',
              })}
            >
              <ReactCodeMirror
                editable={false}
                basicSetup={cmOptions}
                theme={xcodeLight}
                width={'100%'}
                height={'200px'}
                value={curlCommand}
              />
            </div>
          </Tab>
          <Tab key={'JavaScript'} overrides={FileTabStyleObject(theme)} title={'JavaScript'}>
            <div
              className={css({
                width: '100%',
                height: '100%',
              })}
            >
              <ReactCodeMirror
                value={javascriptCommand}
                editable={false}
                basicSetup={cmOptions}
                theme={xcodeLight}
                width={'100%'}
                height={'200px'}
              />
            </div>
          </Tab>
          <Tab key={'Python'} overrides={FileTabStyleObject(theme)} title={'Python'}>
            <div
              className={css({
                width: '100%',
                height: '100%',
              })}
            >
              <ReactCodeMirror
                value={pythonCommand}
                editable={false}
                basicSetup={cmOptions}
                theme={xcodeLight}
                width={'100%'}
                height={'200px'}
              />
            </div>
          </Tab>
        </Tabs>
      </div>
    </>
  );
}
